/** Query keys and shared queries. Server state lives in TanStack Query; the editor keeps its own
 * working copy (features/editor/store.ts). */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "./client";

export const keys = {
  me: ["me"] as const,
  meta: ["meta"] as const,
  newspapers: ["newspapers"] as const,
  newspaper: (id: string) => ["newspaper", id] as const,
  templates: (np: string) => ["templates", np] as const,
  template: (id: string) => ["template", id] as const,
  sponsors: (np: string) => ["sponsors", np] as const,
  reels: (np: string, filters?: object) => (filters ? (["reels", np, filters] as const) : (["reels", np] as const)),
  counts: (np: string, filters?: object) => ["counts", np, filters ?? {}] as const,
  reel: (id: string) => ["reel", id] as const,
  trash: (np: string) => ["trash", np] as const,
  job: (id: number) => ["job", id] as const,
  users: ["users"] as const,
  invites: ["invites"] as const,
  sessions: ["sessions"] as const,
  members: (np: string) => ["members", np] as const,
  archived: ["newspapers", "archived"] as const,
};

export const metaQuery = queryOptions({
  queryKey: keys.meta,
  queryFn: () => unwrap(api.GET("/api/meta")),
  staleTime: Infinity,
});

export const newspapersQuery = queryOptions({
  queryKey: keys.newspapers,
  queryFn: () => unwrap(api.GET("/api/newspapers")),
  staleTime: 60_000,
});

export const newspaperQuery = (id: string) =>
  queryOptions({
    queryKey: keys.newspaper(id),
    queryFn: () => unwrap(api.GET("/api/newspapers/{newspaper_id}", { params: { path: { newspaper_id: id } } })),
  });

export const templatesQuery = (np: string) =>
  queryOptions({
    queryKey: keys.templates(np),
    queryFn: () => unwrap(api.GET("/api/newspapers/{newspaper_id}/templates", { params: { path: { newspaper_id: np } } })),
  });

export const sponsorsQuery = (np: string) =>
  queryOptions({
    queryKey: keys.sponsors(np),
    queryFn: () => unwrap(api.GET("/api/newspapers/{newspaper_id}/sponsors", { params: { path: { newspaper_id: np } } })),
  });

export const reelQuery = (id: string) =>
  queryOptions({
    queryKey: keys.reel(id),
    queryFn: () => unwrap(api.GET("/api/reels/{reel_id}", { params: { path: { reel_id: id } } })),
  });

export function useMeta() {
  return useQuery(metaQuery);
}

export function useNewspapers() {
  return useQuery(newspapersQuery);
}
